import Map, { Marker } from 'react-map-gl';
import { Camera } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import 'mapbox-gl/dist/mapbox-gl.css';

interface DeviceLocationMapProps {
  latitude: number;
  longitude: number;
  deviceName?: string;
  thumbnailUrl?: string;
}

export const DeviceLocationMap = ({ latitude, longitude, deviceName, thumbnailUrl }: DeviceLocationMapProps) => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState(false);
  const isMobile = useIsMobile();

  const fetchMapboxToken = useCallback(async () => {
    setTokenError(false);
    try {
      const { data, error } = await supabase.functions.invoke('getMapboxToken');
      if (error) throw error;
      if (!data?.token) throw new Error('No Mapbox token returned');
      setMapboxToken(data.token);
    } catch (error) {
      console.error('Error fetching Mapbox token:', error);
      setTokenError(true);
    }
  }, []);

  useEffect(() => {
    fetchMapboxToken();
  }, [fetchMapboxToken]);

  // Validate coordinates
  const isValidLatitude = typeof latitude === 'number' && !isNaN(latitude) && latitude >= -90 && latitude <= 90;
  const isValidLongitude = typeof longitude === 'number' && !isNaN(longitude) && longitude >= -180 && longitude <= 180;
  const hasValidCoordinates = isValidLatitude && isValidLongitude;

  if (!mapboxToken) {
    return (
      <div className="w-full h-[220px] bg-muted rounded-lg flex flex-col items-center justify-center gap-3">
        {tokenError ? (
          <>
            <p className="text-sm text-muted-foreground">Couldn't load the map.</p>
            <Button variant="outline" size="sm" onClick={fetchMapboxToken}>Retry loading map</Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Loading map...</p>
        )}
      </div>
    );
  }

  if (!hasValidCoordinates) {
    return (
      <div className="w-full h-[220px] bg-muted rounded-lg flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Invalid coordinates. Cannot display map.</p>
      </div>
    );
  }

  const mapHeight = isMobile ? "180px" : "220px";

  return (
    <div className="w-full rounded-lg overflow-hidden border" style={{ height: mapHeight }}>
      <Map
        mapboxAccessToken={mapboxToken}
        initialViewState={{
          latitude,
          longitude,
          zoom: isMobile ? 12 : 13
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
      >
        <Marker
          latitude={latitude}
          longitude={longitude}
        >
          <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white shadow-lg">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={deviceName || "Device location"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-brand-500 flex items-center justify-center">
                <Camera className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
        </Marker>
      </Map>
    </div>
  );
};
